import React, { useMemo } from 'react';
import { useMessage } from './useMessage';
import { useImageRatios } from './useImageRatios';
import { useOverlay } from './useOverlay';
import { useScrollControl } from './useScrollControls';
import { useFade } from './useFade';
import clsx from 'clsx';
import { useTrackHero } from './useTrackHero';
import { HeroButton } from './HeroButton';

import {
  Btn,
  Buttons,
  Container,
  ImageContainer,
  Img,
  Overlay,
  Message,
  MessageHeader,
  MessageSubHeader,
} from './styled';

export interface IButton {
  backgroundColor: string;
  text: string;
  textColor: string;
  url: string;
}

export interface IImage {
  height: number;
  trackingId: string;
  url: string;
  width: number;
}

interface IHeader {
  fontSizeDesktop: number;
  fontSizeMobile: number;
  text: string;
}

export interface IOverlay {
  desktopUrl: string;
  mobileUrl: string;
  widthDesktop: string;
  widthMobile: string;
}

export interface IMessage {
  backgroundColor: string;
  header: IHeader;
  subHeader?: IHeader;
  textColor: string;
}

export type Mode = 'scroll' | 'fade';

export interface IMultiImageHeroSlice {
  buttons: IButton[];
  desktopImages: IImage[];
  desktopRatio?: string;
  fadeTiming: number;
  message?: IMessage;
  mobileImages: IImage[];
  mobileRatio?: string;
  mode: Mode;
  overlay?: IOverlay;
  trackingId: string;
}

const MultiImageHeroSlice = ({
  buttons,
  desktopImages,
  fadeTiming,
  message,
  mobileImages,
  mode,
  overlay,
  trackingId,
}: IMultiImageHeroSlice) => {
  const { container, desktopContainer, messageRef, mobileContainer } = useScrollControl(
    mode,
    desktopImages,
  );

  const {
    enabled,
    header,
    headerStyleDesktop,
    headerStyleMobile,
    messageStyle,
    subHeader,
    subHeaderStyleDesktop,
    subHeaderStyleMobile,
  } = useMessage(message);

  const { buttons: overlayButtons, overlayDesktop, overlayMobile } = useOverlay(buttons, overlay);

  const { desktopContainerStyle, mobileContainerStyle } = useImageRatios(
    desktopImages,
    mobileImages,
  );

  const index = useFade(mode, desktopImages.length, fadeTiming);

  const trackButtonClick = useTrackHero(
    mode,
    trackingId,
    container,
    index,
    desktopImages?.[0].trackingId,
  );

  const formatedDesktopImages = useMemo(
    () =>
      desktopImages.map((i) => ({
        ...i,
        url: i.url.includes('?') ? i.url.concat('&q=90') : i.url.concat('?q=90'),
      })),
    [desktopImages],
  );

  if (!formatedDesktopImages.length) {
    return null;
  }

  return (
    <>
      {enabled && (
        <Message ref={messageRef} style={messageStyle}>
          <MessageHeader style={headerStyleDesktop} className="hidden-lt-md">
            {header}
          </MessageHeader>
          <MessageHeader style={headerStyleMobile} className="hidden-md">
            {header}
          </MessageHeader>
          {subHeader && (
            <>
              <MessageSubHeader style={subHeaderStyleDesktop} className="hidden-lt-md">
                {subHeader}
              </MessageSubHeader>
              <MessageSubHeader style={subHeaderStyleMobile} className="hidden-md">
                {subHeader}
              </MessageSubHeader>
            </>
          )}
        </Message>
      )}

      <Container ref={container}>
        <ImageContainer
          ref={desktopContainer}
          style={desktopContainerStyle}
          className="hidden-lt-md">
          {formatedDesktopImages.map((img, i) => (
            <Img
              className={clsx(i > 0 && mode === 'fade' && 'fade', i > 0 && index === i && 'show')}
              data-img={img.trackingId}
              key={img.url}
              src={img.url}
            />
          ))}
        </ImageContainer>
        <ImageContainer ref={mobileContainer} style={mobileContainerStyle} className="hidden-md">
          {mobileImages.map((img, i) => (
            <Img
              className={clsx(i > 0 && mode === 'fade' && 'fade', i > 0 && index === i && 'show')}
              data-img={img.trackingId}
              key={img.url}
              src={img.url}
            />
          ))}
        </ImageContainer>

        {overlayDesktop && (
          <Overlay className="hidden-lt-md">
            <img src={overlayDesktop.src} style={overlayDesktop.imgStyle} />

            <Buttons>
              {overlayButtons.map((btn) => (
                <HeroButton
                  button={btn}
                  Component={Btn}
                  href={btn.url}
                  key={btn.url}
                  rounded
                  style={btn.style}
                  trackClickHandler={trackButtonClick}>
                  {btn.text}
                </HeroButton>
              ))}
            </Buttons>
          </Overlay>
        )}

        {overlayMobile && (
          <Overlay className="hidden-md">
            <img src={overlayMobile.src} style={overlayMobile.imgStyle} />

            <Buttons>
              {overlayButtons.map((btn) => (
                <HeroButton
                  button={btn}
                  Component={Btn}
                  href={btn.url}
                  key={btn.url}
                  rounded
                  style={btn.style}
                  trackClickHandler={trackButtonClick}>
                  {btn.text}
                </HeroButton>
              ))}
            </Buttons>
          </Overlay>
        )}
      </Container>
    </>
  );
};

export default MultiImageHeroSlice;
